import React,{useEffect,useMemo,useState}from"react";
import{Autocomplete,Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Paper,TextField,Typography}from"@mui/material";

const API_BASE="/api/purchases/nomina";

function formatMoney(value){
  return Number(value||0).toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function employeeName(employee){
  if(!employee)return"";
  return employee.razon_social||employee.nombre_comercial||"";
}

function paymentStatus(value){
  if(value==="PAGADO")return"Pagado";
  if(value==="PARCIAL")return"Parcial";
  return"Pendiente";
}

function documentStatus(value){
  return value==="ANULADO"?"Anulado":"Registrado";
}

export default function PayrollForm({empresaId,payrollId,isCreate,token,canCreate,canEdit,canDelete,dashboardTheme,navigate,showError,showSuccess,showInfo,outlinedButtonSx,dangerButtonSx,parseError}){
  const[loading,setLoading]=useState(!isCreate);
  const[saving,setSaving]=useState(false);
  const[actionLoading,setActionLoading]=useState(false);
  const[employees,setEmployees]=useState([]);
  const[estado,setEstado]=useState("REGISTRADO");
  const[estadoPago,setEstadoPago]=useState("PENDIENTE");
  const[totalPagado,setTotalPagado]=useState("0.00");
  const[saldoPendiente,setSaldoPendiente]=useState("0.00");
  const[annulDialogOpen,setAnnulDialogOpen]=useState(false);
  const[motivoAnulacion,setMotivoAnulacion]=useState("");
  const[form,setForm]=useState({empleado_id:"",periodo_desde:"",periodo_hasta:"",fecha_nomina:new Date().toISOString().slice(0,10),concepto:"Pago de nómina",total:"",observacion:""});
  const editable=isCreate?canCreate:estado!=="ANULADO"&&canEdit;
  const selectedEmployee=useMemo(()=>employees.find(employee=>employee.id===form.empleado_id)||null,[employees,form.empleado_id]);

  const loadCatalogs=async()=>{
    try{
      const response=await fetch(`${API_BASE}/catalogos/?empresa_id=${empresaId}`,{headers:{Authorization:`Bearer ${token}`}});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      setEmployees(data.empleados||[]);
    }catch{
      showError("Error de conexión al cargar empleados.");
    }
  };

  const loadPayroll=async()=>{
    if(isCreate||!payrollId)return;
    try{
      setLoading(true);
      const response=await fetch(`${API_BASE}/${payrollId}/?empresa_id=${empresaId}`,{headers:{Authorization:`Bearer ${token}`}});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      const item=data.nomina||{};
      setForm({empleado_id:item.empleado_id||"",periodo_desde:item.periodo_desde||"",periodo_hasta:item.periodo_hasta||"",fecha_nomina:item.fecha_nomina||new Date().toISOString().slice(0,10),concepto:item.concepto||"Pago de nómina",total:String(item.total||""),observacion:item.observacion||""});
      setEstado(item.estado||"REGISTRADO");
      setEstadoPago(item.estado_pago||"PENDIENTE");
      setTotalPagado(item.total_pagado||"0.00");
      setSaldoPendiente(item.saldo_pendiente||"0.00");
    }catch{
      showError("Error de conexión al cargar la nómina.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{loadCatalogs();loadPayroll();},[empresaId,payrollId]);

  const validate=()=>{
    if(!form.empleado_id){showInfo("Debe seleccionar un empleado.");return false;}
    if(!form.periodo_desde||!form.periodo_hasta){showInfo("Debe indicar el período de la nómina.");return false;}
    if(form.periodo_desde>form.periodo_hasta){showInfo("La fecha inicial no puede ser posterior a la fecha final.");return false;}
    if(Number(form.total)<=0){showInfo("El total debe ser mayor a cero.");return false;}
    return true;
  };

  const savePayroll=async()=>{
    if(!editable||!validate())return;
    try{
      setSaving(true);
      const body={empresa_id:empresaId,empleado_id:form.empleado_id,periodo_desde:form.periodo_desde,periodo_hasta:form.periodo_hasta,fecha_nomina:form.fecha_nomina,concepto:form.concepto,total:form.total,observacion:form.observacion};
      const response=await fetch(isCreate?`${API_BASE}/crear/`:`${API_BASE}/${payrollId}/`,{method:isCreate?"POST":"PUT",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify(body)});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      showSuccess(data.mensaje||(isCreate?"Nómina registrada correctamente.":"Nómina actualizada correctamente."));
      if(isCreate&&data?.nomina?.id)navigate(`/admin-panel/nomina/${data.nomina.id}`,{replace:true});
      else loadPayroll();
    }catch{
      showError("Error de conexión.");
    }finally{
      setSaving(false);
    }
  };

  const openAnnulDialog=()=>{
    setMotivoAnulacion("");
    setAnnulDialogOpen(true);
  };

  const closeAnnulDialog=()=>{
    if(actionLoading)return;
    setAnnulDialogOpen(false);
    setMotivoAnulacion("");
  };

  const annulPayroll=async()=>{
    if(!motivoAnulacion.trim()){showInfo("Debe indicar el motivo de la anulación.");return;}
    try{
      setActionLoading(true);
      const response=await fetch(`${API_BASE}/${payrollId}/anular/`,{method:"POST",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify({empresa_id:empresaId,motivo:motivoAnulacion.trim()})});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      showSuccess(data.mensaje||"Nómina anulada correctamente.");
      setAnnulDialogOpen(false);
      setMotivoAnulacion("");
      loadPayroll();
    }catch{
      showError("Error de conexión.");
    }finally{
      setActionLoading(false);
    }
  };

  if(loading)return<Typography sx={{py:4,textAlign:"center",color:dashboardTheme.muted_text_color}}>Cargando nómina...</Typography>;

  return(
    <>
      <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:2}}>
        <Box>
          <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>{isCreate?"Nueva nómina":"Detalle de nómina"}</Typography>
          {!isCreate&&<Typography sx={{fontSize:13,fontWeight:500,color:dashboardTheme.muted_text_color,mt:.4}}>{documentStatus(estado)} · {paymentStatus(estadoPago)}</Typography>}
        </Box>
        <Box sx={{display:"flex",gap:1,flexWrap:"wrap"}}>
          <Button variant="outlined" onClick={()=>navigate("/admin-panel/nomina")} sx={outlinedButtonSx}>Regresar</Button>
          {!isCreate&&estado==="REGISTRADO"&&<Button variant="contained" onClick={()=>navigate(`/admin-panel/nomina/${payrollId}/pagos`)} sx={dashboardTheme.buttonStyles.contained}>Ver pagos</Button>}
        </Box>
      </Box>

      <Paper elevation={0} sx={{...dashboardTheme.tableStyles.wrapper,p:{xs:1.5,md:2}}}>
        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5}}>
          <Autocomplete options={employees} value={selectedEmployee} disabled={!editable} getOptionLabel={option=>{const identificacion=option.identificacion||option.ruc||option.cedula||"";return`${employeeName(option)}${identificacion?` - ${identificacion}`:""}`;}} isOptionEqualToValue={(option,value)=>option.id===value.id} filterOptions={(options,{inputValue})=>{const q=inputValue.toLowerCase().trim();if(!q)return options.slice(0,50);return options.filter(employee=>String(employee.razon_social||"").toLowerCase().includes(q)||String(employee.nombre_comercial||"").toLowerCase().includes(q)||String(employee.cedula||"").toLowerCase().includes(q)||String(employee.ruc||"").toLowerCase().includes(q)).slice(0,50);}} onChange={(_event,value)=>setForm(prev=>({...prev,empleado_id:value?.id||""}))} renderInput={params=><TextField {...params} size="small" label="Empleado" placeholder="Buscar empleado..." sx={dashboardTheme.inputStyles}/>} noOptionsText="No se encontraron empleados" openOnFocus/>
          <TextField fullWidth size="small" type="date" label="Fecha de nómina" value={form.fecha_nomina} disabled={!editable} onChange={e=>setForm(prev=>({...prev,fecha_nomina:e.target.value}))} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" type="date" label="Período desde" value={form.periodo_desde} disabled={!editable} onChange={e=>setForm(prev=>({...prev,periodo_desde:e.target.value}))} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" type="date" label="Período hasta" value={form.periodo_hasta} disabled={!editable} onChange={e=>setForm(prev=>({...prev,periodo_hasta:e.target.value}))} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" label="Concepto" value={form.concepto} disabled={!editable} onChange={e=>setForm(prev=>({...prev,concepto:e.target.value}))} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" type="number" label="Valor de nómina" value={form.total} disabled={!editable} inputProps={{min:"0.01",step:"0.01"}} onChange={e=>setForm(prev=>({...prev,total:e.target.value}))} sx={dashboardTheme.inputStyles}/>
        </Box>

        <TextField fullWidth size="small" label="Observación" multiline minRows={2} value={form.observacion} disabled={!editable} onChange={e=>setForm(prev=>({...prev,observacion:e.target.value}))} sx={{mt:1.5,...dashboardTheme.inputStyles}}/>

        {!isCreate&&<Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2,mt:2}}>
          <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Total nómina</Typography><Typography sx={{fontSize:20,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(form.total)}</Typography></Paper>
          <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Pagado</Typography><Typography sx={{fontSize:20,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(totalPagado)}</Typography></Paper>
          <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Saldo pendiente</Typography><Typography sx={{fontSize:20,fontWeight:700,color:Number(saldoPendiente)>0?"#D97706":dashboardTheme.text_color}}>${formatMoney(saldoPendiente)}</Typography></Paper>
        </Box>}

        <Box sx={{display:"flex",justifyContent:"flex-end",gap:1,flexWrap:"wrap",mt:3}}>
          {!isCreate&&estado==="REGISTRADO"&&canDelete&&<Button variant="outlined" onClick={openAnnulDialog} disabled={actionLoading||saving} sx={dangerButtonSx}>Anular</Button>}
          {editable&&<Button variant="contained" onClick={savePayroll} disabled={saving||actionLoading} sx={dashboardTheme.buttonStyles.contained}>{saving?"Guardando...":isCreate?"Registrar nómina":"Guardar cambios"}</Button>}
        </Box>
      </Paper>

      <Dialog open={annulDialogOpen} onClose={closeAnnulDialog} fullWidth maxWidth="sm" PaperProps={{elevation:0,sx:{bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"14px",boxShadow:"none"}}}>
        <DialogTitle sx={{fontWeight:700,color:dashboardTheme.text_color}}>Anular nómina</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize:13.5,color:dashboardTheme.muted_text_color,mb:1.5}}>Esta acción dejará la nómina anulada. Indica el motivo para continuar.</Typography>
          <TextField autoFocus fullWidth size="small" label="Motivo de anulación" multiline minRows={3} value={motivoAnulacion} onChange={e=>setMotivoAnulacion(e.target.value)} sx={dashboardTheme.inputStyles}/>
        </DialogContent>
        <DialogActions sx={{p:2,pt:1}}>
          <Button variant="outlined" onClick={closeAnnulDialog} disabled={actionLoading} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" onClick={annulPayroll} disabled={actionLoading} sx={{minHeight:40,px:2.4,borderRadius:"12px",textTransform:"none",fontWeight:700,bgcolor:"#D32F2F",boxShadow:"none","&:hover":{bgcolor:"#B71C1C",boxShadow:"none"}}}>{actionLoading?"Anulando...":"Anular nómina"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}